import { z } from 'zod/v4';

import { MonetaryValueBackendRuntype } from '../backendTypes';
import { PackagingRuntype } from '../offer';
import { RegionsEnumRuntype } from '../supplierAndStockLocation';
import { IncotermsRuntype } from '../supplierTerms/supplierTermsBackendTypes';

export enum TotalCostOfOwnershipRuleCategory {
    Packaging = 'Packaging',
    Discount = 'Discount',
    Customs = 'Customs',
    Shipping = 'Shipping',
    Other = 'Other',
}
export const TotalCostOfOwnershipRuleCategoryRuntype = z.enum(TotalCostOfOwnershipRuleCategory);

export enum TotalCostOfOwnershipRuleConditionType {
    Packaging = 'Packaging',
    Supplier = 'Supplier',
    CountryOfOrigin = 'CountryOfOrigin',
    CustomsCode = 'CustomsCode',
    Incoterms = 'Incoterms',
    Manufacturer = 'Manufacturer',
}

const PackagingConditionRuntype = z.object({
    condition_type: z.literal(TotalCostOfOwnershipRuleConditionType.Packaging),
    value: PackagingRuntype,
});

const SupplierConditionRuntype = z.object({
    condition_type: z.literal(TotalCostOfOwnershipRuleConditionType.Supplier),
    value: z.string(),
});

const CountryOfOriginConditionRuntype = z.object({
    condition_type: z.literal(TotalCostOfOwnershipRuleConditionType.CountryOfOrigin),
    value: RegionsEnumRuntype,
});

const CustomsCodeConditionRuntype = z.object({
    condition_type: z.literal(TotalCostOfOwnershipRuleConditionType.CustomsCode),
    value: z.union([
        z.object({
            code_type: z.literal('Hts'),
            code: z.string(),
            chapter: z.string().optional(),
            heading: z.string().optional(),
            hs_sub_heading: z.string().optional(),
            us_sub_heading: z.string().optional(),
            statistical_suffix: z.string().optional(),
        }),
        z.object({
            code_type: z.literal('Taric'),
            code: z.string(),
            hs_chapter: z.string().optional(),
            hs_heading: z.string().optional(),
            hs_sub_heading: z.string().optional(),
            cn_sub_heading: z.string().optional(),
            taric_sub_heading: z.string().optional(),
            additional_taric_code: z.string().optional(),
        }),
    ]),
    op: z.union([z.literal('Is'), z.literal('IsNot')]),
});

const IncotermsConditionRuntype = z.object({
    condition_type: z.literal(TotalCostOfOwnershipRuleConditionType.Incoterms),
    value: IncotermsRuntype,
});

const ManufacturerConditionRuntype = z.object({
    condition_type: z.literal(TotalCostOfOwnershipRuleConditionType.Manufacturer),
    value: z.string(),
});

const ConditionRuntype = z.union([
    SupplierConditionRuntype,
    PackagingConditionRuntype,
    CountryOfOriginConditionRuntype,
    IncotermsConditionRuntype,
    CustomsCodeConditionRuntype,
    ManufacturerConditionRuntype,
]);

const FixedAmountAction = z.object({
    type: z.literal('Absolute'),
    value: MonetaryValueBackendRuntype,
});

const PercentageBasedAction = z.object({
    type: z.literal('Relative'),
    value: z.string(),
});

const ActionRuntype = z.union([FixedAmountAction, PercentageBasedAction]);
export type TotalCostOfOwnershipActionDTO = z.infer<typeof ActionRuntype>;

export interface TotalCostOfOwnershipRuleDTO extends z.infer<typeof TotalCostOfOwnershipRuleDTORuntype> {}
export const TotalCostOfOwnershipRuleDTORuntype = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    category: TotalCostOfOwnershipRuleCategoryRuntype,
    action: ActionRuntype,
    conditions: z.array(ConditionRuntype),
    is_valid: z.boolean(),
    updated_at: z.string(),
});

export interface TotalCostOfOwnershipInputDTO extends z.infer<typeof TotalCostOfOwnershipInputDTORuntype> {}
export const TotalCostOfOwnershipInputDTORuntype = z.object({
    name: z.string(),
    description: z.string().nullable(),
    category: TotalCostOfOwnershipRuleCategoryRuntype,
    action: ActionRuntype,
    conditions: z.array(ConditionRuntype),
});
